<template>
    <div>
        <HeaderCardsLayout variant="No-Logo">
            <template #headline>{{ $t('activate-user-header') }}</template>
            <template #icon>
                <ModeOnIcon class="w-12 h-12" />
            </template>
            <template #buttons>
                <LinkButton variant="Neutral-Light" size="Base" :to="{ name: 'users' }">
                    <template #icon>
                        <CloseIcon class="size-5" />
                    </template>
                    {{ $t('cancel') }}
                </LinkButton>
                <Button
                    variant="Neutral-Strong"
                    size="Base"
                    :disabled-explanation="activateUserDisabledExplanation"
                    :status="activateStatus"
                    :status-message="activateStatusMessage"
                    @click="activateUser"
                >
                    <template #icon>
                        <ModeOnIcon class="size-5" />
                    </template>
                    {{ $t('activate-submit') }}
                </Button>
            </template>
            <template #breadcrumb>
                <Breadcrumb :headline="$t('breadcrumb')" :current-page="$t('activate-user-header')">
                    <template #currentIcon>
                        <ModeOnIcon class="inline-block align-sub" />
                    </template>
                    <BreadcrumbLink :target="{ name: 'users' }">
                        <UsersIcon class="mr-1 shrink-0" />
                        {{ $t('users') }}
                    </BreadcrumbLink>
                </Breadcrumb>
            </template>
            <template #body>
                <div class="space-y-4 pt-4">
                    <PaddedContentLeftRight class="space-y-4">
                        <AccentCard :variant="getSsoAccentCardVariant(provider)">
                            <template #header="{ headlineId }">
                                <LoginMethodHeader
                                    :headline-id="headlineId"
                                    :variant="getLoginMethodHeaderVariant(provider)"
                                    :is-gray-scale="false"
                                >
                                    <template #headline>
                                        {{ $t('provider-login-header', { provider }) }}
                                    </template>
                                    <template #icon>
                                        <HelpButton :text="$t('live-data-help-text')"></HelpButton>
                                    </template>
                                </LoginMethodHeader>
                            </template>
                            <div class="flex items-start justify-between gap-4">
                                <div
                                    class="p-2 pl-2.5 flex flex-row items-center gap-2 text-sm text-gray-700 font-light min-h-17"
                                >
                                    <template v-if="dataLoadingState === 'loading'">
                                        <WarningIcon class="size-4 text-gray-500 shrink-0" />
                                        <span>{{ $t('user-data-loading') }}</span>
                                    </template>
                                    <template v-if="dataLoadingState === 'error'">
                                        <WarningIcon class="size-4 text-gray-500 shrink-0" />
                                        <span>{{ $t('data-loading-failed') }}</span>
                                    </template>
                                    <template v-if="dataLoadingState === 'success' && user">
                                        <InitialsWithBackground
                                            v-if="!user.avatarUrl"
                                            class="size-12 leading-12 shrink-0 min-w-12"
                                            :first-name="user.firstName"
                                            :last-name="user.lastName"
                                            variant="Default"
                                        />
                                        <img
                                            v-else
                                            :src="user.avatarUrl"
                                            alt=""
                                            class="rounded-full size-12 object-cover shrink-0 min-w-12"
                                        />
                                        <ContactInfo
                                            :email="user.email ?? undefined"
                                            :room="user.room ?? undefined"
                                            :phone="user.phone ?? undefined"
                                            variant="Default"
                                        />
                                    </template>
                                </div>
                            </div>
                        </AccentCard>
                    </PaddedContentLeftRight>
                    <PaddedContent class="bg-white pt-3">
                        <section>
                            <h2 class="font-semibold text-gray-700 text-xl mb-2">
                                {{ $t('activate-user-result-header') }}
                            </h2>
                            <CheckList>
                                <CheckListItem>
                                    <i18n path="single-sign-on-for-user">
                                        <template #method="{ methodLabel }">
                                            <strong class="font-semibold">{{ methodLabel }}</strong>
                                        </template>
                                    </i18n>
                                </CheckListItem>
                                <CheckListItem>
                                    <i18n path="content-usage-for-user">
                                        <template #action="{ actionLabel }">
                                            <strong class="font-semibold">{{ actionLabel }}</strong>
                                        </template>
                                    </i18n>
                                </CheckListItem>
                                <CheckListItem>
                                    <i18n path="saved-data-for-user">
                                        <template #companyGroup="{ companyGroupLabel }">
                                            <strong class="font-semibold">{{ companyGroupLabel }}</strong>
                                        </template>
                                        <template #name="{ nameLabel }">
                                            <strong class="font-semibold">{{ nameLabel }}</strong>
                                        </template>
                                        <template #position="{ positionLabel }">
                                            <strong class="font-semibold">{{ positionLabel }}</strong>
                                        </template>
                                    </i18n>
                                </CheckListItem>
                                <CheckListItem>
                                    <i18n path="loading-data-for-user">
                                        <template #avatar="{ avatarLabel }">
                                            <strong class="font-semibold">{{ avatarLabel }}</strong>
                                        </template>
                                        <template #contactData="{ contactDataLabel }">
                                            <strong class="font-semibold">{{ contactDataLabel }}</strong>
                                        </template>
                                    </i18n>
                                </CheckListItem>
                                <CheckListItem>
                                    <i18n path="user-list-for-user">
                                        <template #location="{ locationLabel }">
                                            <strong class="font-semibold">{{ locationLabel }}</strong>
                                        </template>
                                    </i18n>
                                </CheckListItem>
                            </CheckList>
                        </section>
                    </PaddedContent>
                    <PaddedContent class="bg-white mt-4 pt-3">
                        <h2 class="font-semibold text-gray-700 text-xl mb-2">
                            {{ $t('permissions') }}
                        </h2>
                        <RadioButtonGroup
                            :label="$t('access-permission')"
                            name="access-permission"
                            :is-required="true"
                            :options="accessPermissionOptions"
                            @change="onChangePermission"
                        />
                    </PaddedContent>
                    <PaddedContent class="bg-white mt-4 pt-3">
                        <div class="flex flex-row items-center gap-x-1.5">
                            <h2 class="font-semibold text-gray-700 text-xl">
                                {{ $t('user-data') }}
                            </h2>
                            <HelpButton :text="$t('user-data-help-text')"></HelpButton>
                        </div>
                        <BaseForm>
                            <template #fields>
                                <FieldGroup>
                                    <TextField
                                        :value="values.firstName"
                                        :label="$t('first-name')"
                                        :error-messages="errors.firstName"
                                        :required="true"
                                        :disabled="true"
                                        @input="setFieldValue('firstName', ($event.target as HTMLInputElement).value)"
                                        @blur="setFieldTouched('firstName', true)"
                                    ></TextField>
                                    <TextField
                                        :value="values.lastName"
                                        :label="$t('last-name')"
                                        :error-messages="errors.lastName"
                                        :required="true"
                                        :disabled="true"
                                        @input="setFieldValue('lastName', ($event.target as HTMLInputElement).value)"
                                        @blur="setFieldTouched('lastName', true)"
                                    ></TextField>
                                    <PresetTextField
                                        :value="values.company"
                                        :label="$t('company')"
                                        :presets="companyPresets"
                                        :error-messages="errors.company"
                                        :required="false"
                                        :disabled="true"
                                        @input="setFieldValue('company', ($event.target as HTMLInputElement).value)"
                                        @blur="setFieldTouched('company', true)"
                                    ></PresetTextField>
                                    <TextField
                                        :value="values.position"
                                        :label="$t('position')"
                                        :error-messages="errors.position"
                                        :required="false"
                                        :disabled="true"
                                        @input="setFieldValue('position', ($event.target as HTMLInputElement).value)"
                                        @blur="setFieldTouched('position', true)"
                                    ></TextField>
                                    <TextAreaField
                                        :value="values.notes"
                                        :label="$t('notes')"
                                        :error-messages="errors.notes"
                                        :required="false"
                                        :max-length="userActivateSchema.shape.notes.maxLength ?? undefined"
                                        :help-button-text="$t('user-notes-help-button-text')"
                                        @input="setFieldValue('notes', ($event.target as HTMLInputElement).value)"
                                        @blur="setFieldTouched('notes', true)"
                                    >
                                    </TextAreaField>
                                </FieldGroup>
                            </template>
                        </BaseForm>
                    </PaddedContent>
                </div>
            </template>
        </HeaderCardsLayout>
    </div>
</template>
<script setup lang="ts">
import auth from '@/auth/auth.ts';
import AccentCard from '@/components/AccentCard/AccentCard.vue';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue';
import BreadcrumbLink from '@/components/Breadcrumb/BreadcrumbLink.vue';
import { ButtonStatus } from '@/components/Button/Button.types';
import Button from '@/components/Button/Button.vue';
import LinkButton from '@/components/Button/LinkButton.vue';
import CheckList from '@/components/CheckList/CheckList.vue';
import CheckListItem from '@/components/CheckList/CheckListItem.vue';
import ContactInfo from '@/components/ContactInfo/ContactInfo.vue';
import BaseForm from '@/components/Form/BaseForm.vue';
import FieldGroup from '@/components/Form/FieldGroup.vue';
import PresetTextField from '@/components/Form/PresetTextField.vue';
import RadioButtonGroup from '@/components/Form/RadioButtonGroup.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import TextField from '@/components/Form/TextField.vue';
import HelpButton from '@/components/HelpButton/HelpButton.vue';
import CloseIcon from '@/components/Icon/CloseIcon.vue';
import KoelncongressLogo from '@/components/Icon/KoelncongressLogo.vue';
import KoelnmesseLogo from '@/components/Icon/KoelnmesseLogo.vue';
import ModeOnIcon from '@/components/Icon/ModeOnIcon.vue';
import UsersIcon from '@/components/Icon/UsersIcon.vue';
import WarningIcon from '@/components/Icon/WarningIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import PaddedContentLeftRight from '@/components/Layout/PaddedContentLeftRight.vue';
import LoginMethodHeader from '@/components/LoginMethodHeader/LoginMethodHeader.vue';
import { useForm } from '@/forms/composables/useForm';
import { SsoProvider } from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import router from '@/plugins/router';
import { computed, markRaw, ref } from 'vue';
import InitialsWithBackground from '../components/InitialsWithBackground.vue';
import { useActivateEntraUser } from '../composables/useActivateEntraUser';
import { useGetUserEntraData } from '../composables/useGetUserEntraData';
import { userActivateSchema } from '../forms/schemas';
import { getLoginMethodHeaderVariant, getSsoAccentCardVariant } from '../sso';

const props = defineProps<{ oid: string; provider: SsoProvider }>();

const oid = computed(() => props.oid);
const provider = computed(() => props.provider);

const { user, errors: loadEntraDataErrors, loading } = useGetUserEntraData(oid, provider);

const dataLoadingState = computed(() => {
    if (loading.value) {
        return 'loading';
    } else if (loadEntraDataErrors.value && loadEntraDataErrors.value.length > 0) {
        return 'error';
    }
    return 'success';
});

const { values, errors, setFieldValue, setFieldTouched, validate, hasErrors } = useForm(
    userActivateSchema,
    computed(() => ({
        firstName: user.value?.firstName ?? '',
        lastName: user.value?.lastName ?? '',
        company: user.value?.company ?? '',
        position: user.value?.position ?? '',
        notes: '',
    })),
);

const companyPresets = [
    {
        value: 'Koelnmesse GmbH',
        label: 'Koelnmesse GmbH',
        icon: markRaw(KoelnmesseLogo),
        iconClass: 'h-4.5',
        selectedClass: 'bg-koelnmesse',
    },
    {
        value: 'Koelncongress GmbH',
        label: 'Koelncongress GmbH',
        icon: markRaw(KoelncongressLogo),
        iconClass: 'h-4',
        selectedClass: 'bg-koelnmesse',
    },
];

const { activate, isActivating } = useActivateEntraUser();
const hasActivateError = ref(false);

const activateStatus = computed<ButtonStatus>(() => {
    if (hasActivateError.value) {
        return 'Error';
    }

    if (isActivating.value) {
        return 'Loading';
    }

    return 'Default';
});

const activateStatusMessage = computed(() => (hasActivateError.value ? $t('activate-user-failed') : undefined));

async function activateUser() {
    const isValid = await validate();

    if (!isValid) {
        return;
    }

    hasActivateError.value = false;

    try {
        const userId = await activate({
            oid: oid.value,
            provider: provider.value,
            firstName: values.value.firstName,
            lastName: values.value.lastName,
            company: values.value.company,
            position: values.value.position,
            notes: values.value.notes,
        });
        router.replace({ name: 'user-details', params: { userId } });
    } catch {
        hasActivateError.value = true;
    }
}

const accessPermissionOptions = [
    {
        id: 'read-and-write',
        label: $t('read-and-write'),
        isChecked: false,
        isDisabled: false,
        value: 'read-and-write',
    },
    {
        id: 'read',
        label: $t('read'),
        isChecked: true,
        isDisabled: false,
        value: 'read',
    },
    {
        id: 'no-read-or-write',
        label: $t('no-read-or-write'),
        isChecked: false,
        isDisabled: true,
        value: 'no-read-or-write',
        helpButtonText: $t('no-read-or-write-help'),
    },
];

const permissionChange = ref(false);

function onChangePermission(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (value === 'read-and-write') {
        permissionChange.value = true;
    } else {
        permissionChange.value = false;
    }
}

const activateUserDisabledExplanation = computed(() => {
    if (!auth.isAdmin()) return $t('no-permission-to-activate');
    if (hasErrors.value) return $t('invalid-form-fields');
});
</script>
